import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Box,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Star as StarIcon,
  Code as CodeIcon,
  Language as LanguageIcon,
  GitHub as GitHubIcon,
} from '@mui/icons-material';

// Helper to convert kebab-case or snake_case to Title Case
function toTitleCase(str) {
  return str
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const ProjectCard = ({
  name,
  description,
  url,
  homepage,
  topics = [],
  stars,
  language,
  updatedAt,
  screenshot,
}) => {
  return (
    <Card 
      elevation={1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        minWidth: 320,
        maxWidth: 400,
        p: 1,
        '&:hover': {
          elevation: 3,
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              wordBreak: 'break-word',
              lineHeight: 1.2,
              mb: 0,
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              letterSpacing: 0.5,
            }}
          >
            <a
              href={url || homepage}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'inherit',
                textDecoration: 'none',
                wordBreak: 'break-word',
                display: 'inline',
              }}
            >
              {toTitleCase(name)}
            </a>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {homepage && (
              <Tooltip title="Visit Live Site">
                <IconButton 
                  size="small" 
                  href={homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'text.secondary' }}
                >
                  <LanguageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View Source Code">
              <IconButton 
                size="small" 
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary' }}
              >
                <GitHubIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Divider sx={{ mb: 2, bgcolor: '#384E77', height: 3, opacity: 0.2 }} />

        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 2 }}
        >
          {description}
        </Typography>

        {screenshot && (
          <Box sx={{ width: '100%', aspectRatio: '16/9', overflow: 'hidden', borderRadius: 2, mb: 2 }}>
            <img
              src={screenshot}
              alt={`${name} screenshot`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )}

        {(homepage || url) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="primary" component="a" href={homepage || url} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all', textDecoration: 'underline' }}>
              {homepage || url}
            </Typography>
          </Box>
        )}

        {topics.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {topics.map((topic) => (
              <Chip
                key={topic}
                label={topic}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  backgroundColor: 'action.hover',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                }}
              />
            ))}
          </Box>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ 
        px: 2, 
        py: 1, 
        bgcolor: 'action.hover',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {stars !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {stars}
              </Typography>
            </Box>
          )}
          {language && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CodeIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {language}
              </Typography>
            </Box>
          )}
        </Box>
        {updatedAt && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              fontSize: '0.75rem',
              cursor: 'help',
            }}
            title={new Date(updatedAt).toLocaleString()}
          >
            Updated {new Date(updatedAt).toLocaleDateString()}
          </Typography>
        )}
      </CardActions>
    </Card>
  );
};

export default ProjectCard; 